'use client';

import { useEffect, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { getConsentStatus } from './CookieConsentBanner';

const GA_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;

function loadGtag(measurementId: string) {
  if (document.getElementById('gtag-script')) return;

  const script = document.createElement('script');
  script.id = 'gtag-script';
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  script.async = true;
  document.head.appendChild(script);

  (window as any).dataLayer = (window as any).dataLayer || [];
  function gtag(...args: any[]) {
    (window as any).dataLayer.push(args);
  }
  (window as any).gtag = gtag;
  gtag('js', new Date());
  gtag('config', measurementId, { anonymize_ip: true });
}

function trackPageView(url: string) {
  if (typeof window !== 'undefined' && (window as any).gtag && GA_ID) {
    (window as any).gtag('event', 'page_view', { page_path: url });
  }
}

export function trackEvent(eventName: string, eventParams: Record<string, any> = {}) {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', eventName, eventParams);
  }
}

export default function GoogleAnalytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const initialised = useRef(false);

  // Initialise GA if consent already given on mount
  useEffect(() => {
    if (!GA_ID) return;
    const status = getConsentStatus();
    if (status === 'accepted') {
      loadGtag(GA_ID);
      initialised.current = true;
    }
  }, []);

  // Listen for consent changes (user clicks Accept in the banner)
  useEffect(() => {
    if (!GA_ID) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail === 'accepted' && !initialised.current) {
        loadGtag(GA_ID);
        initialised.current = true;
        // Track the current page immediately after consent
        const url = pathname + (searchParams.toString() ? `?${searchParams.toString()}` : '');
        trackPageView(url);
      }
    };
    window.addEventListener('cookieConsentUpdate', handler);
    return () => window.removeEventListener('cookieConsentUpdate', handler);
  }, [pathname, searchParams]);

  // Track page views on route changes (only if GA is loaded)
  useEffect(() => {
    if (!GA_ID || !initialised.current) return;
    const url = pathname + (searchParams.toString() ? `?${searchParams.toString()}` : '');
    trackPageView(url);
  }, [pathname, searchParams]);

  return null;
}
